import { InjectionToken } from '@angular/core';

import { ConfirmDialogConfig } from '@ninety/platform/models/_shared/confirm-dialog-config';
import { DowngradeDialogData } from '@ninety/platform/models/billing/downgrade-dialog-data';
import { RetentionDialogData } from '@ninety/platform/models/billing/retention-dialog-data';
import { ConfirmLicenseChangeDialogData } from '@ninety/platform/models/billingv2/confirm-license-change-dialog-data';

import { DialogOpener, noopDialogRef } from './dialog-opener.type';

/**
 * Dialog-opener tokens for the billing + subscription dialog components (DEV-26105 T6b, Option B —
 * same token-inversion mechanism as T6a's `auth-dialog.tokens`). The concrete components live in
 * `shared/ui/core` (`_components/billing-dialogs/*` + the shared `ConfirmDialogComponent`); importing
 * them from `billing-state.effects` / `subscription-state.effects` would reopen the
 * `platform/state -> shared/ui/core` edge this epic exists to sever. Each token defaults to a no-op
 * opener and is bound at the app composition root via `billing-dialog.providers.ts`.
 */

// --- billing-state.effects dialogs ---

export const CHECKOUT_DIALOG = new InjectionToken<DialogOpener<{ subscriptionConfigurationId: string }>>(
  'CHECKOUT_DIALOG',
  { providedIn: 'root', factory: (): DialogOpener<{ subscriptionConfigurationId: string }> => () => noopDialogRef() }
);

/**
 * `In` is `unknown` (not `{ currentPlan: PricingPlan; … }`) because the effect passes the
 * `selectCurrentSubscriptionConfig` value (`SubscriptionConfigurationV3`) as `currentPlan`, which is
 * structurally distinct from the component's declared `PricingPlan`. The pre-T6b call site
 * (`dialog.open<PricingTierDialogComponent>(…)`) left the dialog data untyped; keeping `In` loose
 * preserves that exact behavior without retyping the selector or component (out of scope for T6b).
 */
export const PRICING_TIER_DIALOG = new InjectionToken<DialogOpener>('PRICING_TIER_DIALOG', {
  providedIn: 'root',
  factory: (): DialogOpener => () => noopDialogRef(),
});

export const CONFIRM_LICENSE_CHANGE_DIALOG = new InjectionToken<DialogOpener<ConfirmLicenseChangeDialogData, number>>(
  'CONFIRM_LICENSE_CHANGE_DIALOG',
  {
    providedIn: 'root',
    factory: (): DialogOpener<ConfirmLicenseChangeDialogData, number> => () => noopDialogRef(),
  }
);

export const DOWNGRADE_CONFIRMATION_DIALOG = new InjectionToken<DialogOpener<DowngradeDialogData>>(
  'DOWNGRADE_CONFIRMATION_DIALOG',
  { providedIn: 'root', factory: (): DialogOpener<DowngradeDialogData> => () => noopDialogRef() }
);

export const DOWNGRADE_RETENTION_DIALOG = new InjectionToken<DialogOpener<RetentionDialogData>>(
  'DOWNGRADE_RETENTION_DIALOG',
  { providedIn: 'root', factory: (): DialogOpener<RetentionDialogData> => () => noopDialogRef() }
);

export const DOWNGRADE_RETENTION_STEP2_DIALOG = new InjectionToken<DialogOpener<RetentionDialogData>>(
  'DOWNGRADE_RETENTION_STEP2_DIALOG',
  { providedIn: 'root', factory: (): DialogOpener<RetentionDialogData> => () => noopDialogRef() }
);

export const CONFIRM_DIALOG = new InjectionToken<DialogOpener<ConfirmDialogConfig, boolean>>('CONFIRM_DIALOG', {
  providedIn: 'root',
  factory: (): DialogOpener<ConfirmDialogConfig, boolean> => () => noopDialogRef(),
});

// --- subscription-state.effects dialogs ---

export const MANAGE_SEATS_DIALOG = new InjectionToken<DialogOpener>('MANAGE_SEATS_DIALOG', {
  providedIn: 'root',
  factory: (): DialogOpener => () => noopDialogRef(),
});
